import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Dropdown, Form, Textbox, useForm } from "capitalrx-components-2.0/lib/forms";
import { Color, Icon, Message, MessageAppearance, Tooltip } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading, Semibold } from "capitalrx-components-2.0/lib/typography";

import { MedicarePrescriptionPaymentPlanEstimateList, getPlanAmount, getPlanEstimatePayments } from "@/api/axios";
import { MONTHS } from "@/basic/utils/months";
import { useSelectedEnrollmentPlan } from "@/premium/hooks/enrollmentPlan";
import { ThemedSubmitButton } from "@/premium/themedComponents";
import { ANALYTIC_EVENTS, logEvent } from "@/utils/analytics";

import { formatDollars } from "capitalrx-utilities";
import { PaymentBarChart } from "./PaymentBarChart";
import PaymentsTable from "./PaymentsTable";

const cleanValue = (v: string) => {
  return v.replace(/[^0-9.]/, "");
};

const MEDICARE_ANNUAL_OOP_MAXIMUMS: { [year: number]: number } = {
  2025: 2000,
  2026: 2100,
};

interface PaymentCalculatorForm {
  readonly expectedMonthlyCosts: number;
  readonly beginningMonth: string;
  readonly year: number;
}

export const PaymentCalculator = () => {
  const { t: translate } = useTranslation();
  const [planEstimates, setPlanEstimates] = useState<MedicarePrescriptionPaymentPlanEstimateList>();
  const [troopMax, setTroopMax] = useState<number>(0);
  const [planEffectiveDate, setPlanEffectiveDate] = useState<Date>(null);
  const { enrollmentPlanCsid } = useSelectedEnrollmentPlan();
  const now = DateTime.now();

  const medicarePrescriptionPlanMinDate = DateTime.fromFormat("20250101", "yyyyLLdd");
  const startYear = medicarePrescriptionPlanMinDate.year;
  const years = useMemo(() => {
    if (planEffectiveDate && planEffectiveDate.getUTCFullYear() >= startYear) {
      return [planEffectiveDate.getUTCFullYear(), planEffectiveDate.getUTCFullYear() + 1];
    }
    return [now.year, now.year + 1];
  }, [planEffectiveDate]);
  const availableYears = years.filter((y) => y >= startYear);

  const paymentCalculatorForm = useForm<PaymentCalculatorForm>({
    defaultValues: {
      year: DateTime.max(now, medicarePrescriptionPlanMinDate).year,
      beginningMonth: DateTime.max(now, medicarePrescriptionPlanMinDate).month.toString(),
      expectedMonthlyCosts: null,
    },
  });
  const monthlyCost = paymentCalculatorForm.watch("expectedMonthlyCosts");
  const selectedYear = paymentCalculatorForm.watch("year");

  const MessageBoxTypeAppearance: MessageAppearance = {
    icon: "info-circle",
    backgroundColor: Color.Red[50],
    borderColor: Color.Red[200],
    iconColor: Color.Red[500],
  };

  const anyPaymentExceedsExpected = useMemo(() => {
    if (planEstimates) {
      return !!planEstimates.results.find((payment) => {
        const troopAmount = Number(payment?.estimation?.true_out_of_pocket_amount || 0);
        const billedAmount = Number(payment?.estimation?.billed_amount || 0);
        return troopAmount > 0 && billedAmount > +monthlyCost;
      });
    } else {
      return false;
    }
  }, [planEstimates]);

  useEffect(() => {
    if (enrollmentPlanCsid) {
      getPlanAmount(enrollmentPlanCsid.id).then((data) => {
        setPlanEffectiveDate(new Date(data.effective_date));
        paymentCalculatorForm.setValue("beginningMonth", String(new Date(data.effective_date).getUTCMonth() + 1));
        paymentCalculatorForm.setValue("year", new Date(data.effective_date).getUTCFullYear());
      });
    }
  }, [enrollmentPlanCsid]);

  useEffect(() => {
    if (selectedYear) {
      const maxOOP = MEDICARE_ANNUAL_OOP_MAXIMUMS[selectedYear] ?? 2100;
      setTroopMax(maxOOP);
    }
  }, [selectedYear]);

  const handleFormSubmit = useCallback(
    async (formData: PaymentCalculatorForm) => {
      logEvent(ANALYTIC_EVENTS.PAYMENT_CALCULATOR);
      const formattedValue = cleanValue(String(formData.expectedMonthlyCosts));
      const estimate = await getPlanEstimatePayments(
        enrollmentPlanCsid.id,
        Number(formattedValue),
        formData.year,
        +formData.beginningMonth,
      );
      setPlanEstimates(estimate);
    },
    [enrollmentPlanCsid],
  );
  const { columns } = useResponsive({
    columns: {
      mobile: 12,
      desktop: 2,
    },
  });

  const BottomAlignedStack = styled(Stack)`
    height: 100%;
    justify-content: flex-end;
  `;

  return (
    <Stack spacing="large">
      <Stack spacing="medium">
        <Heading>{translate("MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentCalculator.paymentCalculator")}</Heading>
        <Body>{translate("MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentCalculator.useThisTool")} </Body>
      </Stack>
      <Form form={paymentCalculatorForm} onSubmit={handleFormSubmit}>
        <Grid spacing="mini">
          <Grid.Span columns={columns}>
            <BottomAlignedStack spacing="mini">
              <Semibold>
                <span>{translate("MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentCalculator.maxOOP")}</span>
                <Tooltip content={translate("MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentCalculator.thisIsTheMaximumAmount")}>
                  {(ref) => (
                    <span ref={ref}>
                      <Icon size="small" name="info-circle" />
                    </span>
                  )}
                </Tooltip>
              </Semibold>
              <Body>{formatDollars(troopMax)}</Body>
            </BottomAlignedStack>
          </Grid.Span>

          <Grid.Span columns={columns}>
            <Textbox.Field label={translate("MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentCalculator.expectedMonthlyCost")} name="expectedMonthlyCosts" required />
          </Grid.Span>

          <Grid.Span columns={columns}>
            <Dropdown.Field required name="beginningMonth" label={translate("MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentCalculator.beginningMonth")}>
              {Object.keys(MONTHS).map((monthNumber) => {
                const monthName = MONTHS[+monthNumber];
                return (
                  <Dropdown.Option key={monthNumber} value={monthNumber}>
                    {translate(`MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentCalculator.${monthName}`)}
                  </Dropdown.Option>
                );
              })}
            </Dropdown.Field>
          </Grid.Span>

          <Grid.Span columns={columns}>
            <Dropdown.Field required name="year" label={translate("MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentCalculator.year")}>
              {availableYears.map((year) => {
                return (
                  <Dropdown.Option key={year} value={year}>
                    {year}
                  </Dropdown.Option>
                );
              })}
            </Dropdown.Field>
          </Grid.Span>

          <Grid.Span columns={columns}>
            <Shelf>
              <Stack spacing="mini">
                {/* I know the input alignment container exists but for some reason there's some spacing issues and I can't figure it out */}
                <Body inline size="mini">
                  <Semibold>&nbsp;</Semibold>
                </Body>
                <ThemedSubmitButton>{translate("MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentCalculator.calculate")}</ThemedSubmitButton>
              </Stack>
              <Shelf.Spacer />
            </Shelf>
          </Grid.Span>
        </Grid>
      </Form>
      <Body>
        <Message appearance={MessageBoxTypeAppearance} visible={anyPaymentExceedsExpected}>
          {translate("MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentCalculator.message")}
        </Message>
      </Body>
      {planEstimates && <PaymentBarChart {...planEstimates} />}
      {planEstimates && <PaymentsTable {...planEstimates} />}
    </Stack>
  );
};
